import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { leadsAPI, campaignsAPI, logsAPI, campaignStepsAPI } from '../services/api';
import Card from '../components/Card';
import Button from '../components/Button';
import { TableSkeleton } from '../components/LoadingSpinner';
import { useToast } from '../hooks/useToast';
import './LeadDetail.css';

export default function LeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [lead, setLead] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignSteps, setCampaignSteps] = useState({}); // {campaignId: [steps]}
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [personalMessages, setPersonalMessages] = useState({}); // {campaignId: {stepOrder: message}}
  const [editingMessage, setEditingMessage] = useState(null); // {campaignId, stepOrder}

  useEffect(() => {
    if (leadId) {
      loadLeadData();
      // Set up auto-refresh every 30 seconds
      const interval = setInterval(() => {
        loadLeadData(false); // Silent refresh
      }, 30000);
      
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const loadLeadData = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    
    try {
      const [leadResponse, logsResponse, assignmentsResponse] = await Promise.all([
        leadsAPI.get(leadId),
        logsAPI.getAll({ lead_id: leadId }),
        campaignsAPI.getLeadAssignments()
      ]);

      const leadData = leadResponse.data;
      setLead(leadData);

      // Get logs
      const logsData = Array.isArray(logsResponse.data) 
        ? logsResponse.data 
        : (logsResponse.data?.results || []);
      setLogs(logsData);

      // Get campaign assignments for this lead
      const assignments = assignmentsResponse.data?.lead_assignments || {};
      const leadCampaigns = assignments[leadId] || [];
      
      // Fetch full campaign details
      const campaignPromises = leadCampaigns.map(c => campaignsAPI.get(c.campaign_id));
      const campaignResponses = await Promise.all(campaignPromises);
      const campaignData = campaignResponses.map(r => r.data);
      setCampaigns(campaignData);

      // Fetch steps for each campaign
      const stepsPromises = campaignData.map(c => campaignStepsAPI.getAll(c.id));
      const stepsResponses = await Promise.all(stepsPromises);
      const stepsMap = {};
      campaignData.forEach((campaign, index) => {
        const steps = Array.isArray(stepsResponses[index].data) 
          ? stepsResponses[index].data 
          : (stepsResponses[index].data?.results || []);
        stepsMap[campaign.id] = steps.sort((a, b) => a.step_order - b.step_order);
      });
      setCampaignSteps(stepsMap);

      // Load personal messages
      setPersonalMessages(leadData.personal_messages || {});

      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to load lead data:', error);
      if (error.response?.status === 404) {
        toast.error('Lead not found');
        navigate('/leads');
      } else {
        toast.error('Failed to load lead data. Check console for details.');
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const pauseLead = async () => {
    if (!window.confirm('Pause this lead? Outreach will stop in all campaigns.')) {
      return;
    }

    try {
      setActionLoading('pause');
      await leadsAPI.pause(leadId);
      toast.success('Lead paused successfully - will not be processed in any campaigns');
      await loadLeadData(false);
    } catch (error) {
      console.error('Failed to pause lead:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Failed to pause lead';
      toast.error(errorMsg);
    } finally {
      setActionLoading(null);
    }
  };

  const resumeLead = async () => {
    if (!window.confirm('Resume this lead? Outreach will continue in assigned campaigns.')) {
      return;
    }

    try {
      setActionLoading('resume');
      await leadsAPI.resume(leadId);
      toast.success('Lead resumed successfully - will be processed in campaigns');
      await loadLeadData(false);
    } catch (error) {
      console.error('Failed to resume lead:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Failed to resume lead';
      toast.error(errorMsg);
    } finally {
      setActionLoading(null);
    }
  };

  const isPaused = () => {
    if (!lead?.next_action_date) return false;
    const actionDate = new Date(lead.next_action_date);
    const now = new Date();
    const yearsDiff = (actionDate - now) / (1000 * 60 * 60 * 24 * 365);
    return yearsDiff > 50; // Far future = paused
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'NEW': 'var(--info)',
      'CONTACTED': 'var(--secondary)',
      'ENGAGED': 'var(--success)',
      'REPLIED': 'var(--success)',
      'BOOKED': 'var(--success)',
      'NOT_INTERESTED': 'var(--error)',
      'DEAD': 'var(--gray-700)',
    };
    return statusMap[status] || 'var(--text-secondary)';
  };

  const getLogStatusColor = (status) => {
    const statusMap = {
      'PENDING': 'var(--warning)',
      'SENT': 'var(--success)',
      'FAILED': 'var(--error)',
      'DELIVERED': 'var(--success)',
      'BOUNCED': 'var(--error)',
    };
    return statusMap[status] || 'var(--text-secondary)';
  };

  if (loading) {
    return (
      <div className="content-wrapper">
        <div className="page-header">
          <h1 className="page-title">Lead Details</h1>
        </div>
        <Card>
          <TableSkeleton rows={5} cols={3} />
        </Card>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="content-wrapper">
        <div className="page-header">
          <h1 className="page-title">Lead Not Found</h1>
        </div>
        <Card>
          <div className="empty-state">
            <p>The lead you're looking for doesn't exist.</p>
            <Button variant="primary" onClick={() => navigate('/leads')}>
              Back to Leads
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const paused = isPaused();

  return (
    <div className="content-wrapper">
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-xs)' }}>
            <Link to="/leads" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>
              ← Back to Leads
            </Link>
          </div>
          <h1 className="page-title">
            {lead.first_name} {lead.last_name}
            {paused && (
              <span 
                style={{ 
                  marginLeft: '12px',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--warning)',
                  fontWeight: 'var(--font-weight-normal)'
                }}
              >
                ⏸ Paused
              </span>
            )}
          </h1>
          <p className="page-subtitle">{lead.email}</p>
        </div>
        <div className="page-actions">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => loadLeadData(false)}
            title="Refresh data"
          >
            ↻ Refresh
          </Button>
          <Button
            variant="warning"
            size="sm"
            onClick={pauseLead}
            loading={actionLoading === 'pause'}
            disabled={lead.status === 'DEAD' || lead.status === 'NOT_INTERESTED' || paused}
            title={paused ? 'Lead is already paused' : 'Pause this lead in all campaigns'}
          >
            Pause
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={resumeLead}
            loading={actionLoading === 'resume'}
            disabled={!paused}
            title={!paused ? 'Lead is already active' : 'Resume this lead in all campaigns'}
          >
            Resume
          </Button>
        </div>
      </div>

      {lastUpdated && (
        <div style={{ 
          marginBottom: 'var(--spacing-md)', 
          fontSize: 'var(--font-size-sm)', 
          color: 'var(--text-secondary)' 
        }}>
          Last updated: {lastUpdated.toLocaleTimeString()}
        </div>
      )}

      <div className="lead-detail-grid">
        {/* Contact Information */}
        <Card>
          <h2 className="card-title">Contact Information</h2>
          <div className="detail-section">
            <div className="detail-row">
              <span className="detail-label">Email:</span>
              <span className="detail-value">
                <a href={`mailto:${lead.email}`} style={{ color: 'var(--primary)' }}>
                  {lead.email}
                </a>
              </span>
            </div>
            {lead.phone && (
              <div className="detail-row">
                <span className="detail-label">Phone:</span>
                <span className="detail-value">
                  <a href={`tel:${lead.phone}`} style={{ color: 'var(--primary)' }}>
                    {lead.phone}
                  </a>
                </span>
              </div>
            )}
            {lead.country && (
              <div className="detail-row">
                <span className="detail-label">Country:</span>
                <span className="detail-value">{lead.country}</span>
              </div>
            )}
            {lead.timezone && (
              <div className="detail-row">
                <span className="detail-label">Timezone:</span>
                <span className="detail-value">{lead.timezone}</span>
              </div>
            )}
            {lead.facebook_user_id && (
              <div className="detail-row">
                <span className="detail-label">Facebook User ID:</span>
                <span className="detail-value" style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-sm)' }}>
                  {lead.facebook_user_id}
                </span>
              </div>
            )}
            {lead.instagram_user_id && (
              <div className="detail-row">
                <span className="detail-label">Instagram User ID:</span>
                <span className="detail-value" style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-sm)' }}>
                  {lead.instagram_user_id}
                </span>
              </div>
            )}
            {lead.facebook_page_id && (
              <div className="detail-row">
                <span className="detail-label">Facebook Page ID:</span>
                <span className="detail-value" style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-sm)' }}>
                  {lead.facebook_page_id}
                </span>
              </div>
            )}
          </div>
        </Card>

        {/* Status & Priority */}
        <Card>
          <h2 className="card-title">Status & Priority</h2>
          <div className="detail-section">
            <div className="detail-row">
              <span className="detail-label">Status:</span>
              <span className="detail-value">
                <span 
                  className="status-badge" 
                  style={{ 
                    background: `${getStatusColor(lead.status)}20`,
                    color: getStatusColor(lead.status),
                    border: `1px solid ${getStatusColor(lead.status)}40`,
                    padding: '4px 12px',
                    borderRadius: 'var(--border-radius)',
                    fontWeight: 'var(--font-weight-semibold)'
                  }}
                >
                  {lead.status}
                </span>
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Priority Score:</span>
              <span className="detail-value">
                <span className="badge" style={{
                  background: (lead.priority_score || 0) >= 7 ? 'rgba(220, 38, 38, 0.15)' :
                             (lead.priority_score || 0) >= 4 ? 'rgba(251, 191, 36, 0.15)' :
                             'rgba(107, 114, 128, 0.15)',
                  color: (lead.priority_score || 0) >= 7 ? 'var(--error)' :
                         (lead.priority_score || 0) >= 4 ? 'var(--warning)' :
                         'var(--text-secondary)',
                  padding: '4px 12px',
                  borderRadius: 'var(--border-radius)',
                  fontWeight: 'var(--font-weight-semibold)'
                }}>
                  {lead.priority_score || 0}
                </span>
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Next Action:</span>
              <span className="detail-value">
                {lead.next_action_date
                  ? (paused ? (
                      <span style={{ color: 'var(--warning)' }}>Paused</span>
                    ) : (
                      new Date(lead.next_action_date).toLocaleString()
                    ))
                  : 'Not scheduled'}
              </span>
            </div>
            {lead.last_contacted_at && (
              <div className="detail-row">
                <span className="detail-label">Last Contacted:</span>
                <span className="detail-value">
                  {new Date(lead.last_contacted_at).toLocaleString()}
                </span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">Created:</span>
              <span className="detail-value">
                {new Date(lead.created_at).toLocaleString()}
              </span>
            </div>
          </div>
        </Card>

        {/* Channel Permissions */}
        <Card>
          <h2 className="card-title">Channel Permissions</h2>
          <div className="detail-section">
            <div className="detail-row">
              <span className="detail-label">Email:</span>
              <span className="detail-value">
                <span style={{ 
                  color: lead.allow_email ? 'var(--success)' : 'var(--error)',
                  fontWeight: 'var(--font-weight-semibold)'
                }}>
                  {lead.allow_email ? '✓ Allowed' : '✗ Not Allowed'}
                </span>
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">SMS:</span>
              <span className="detail-value">
                <span style={{ 
                  color: lead.allow_sms ? 'var(--success)' : 'var(--error)',
                  fontWeight: 'var(--font-weight-semibold)'
                }}>
                  {lead.allow_sms ? '✓ Allowed' : '✗ Not Allowed'}
                </span>
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Facebook:</span>
              <span className="detail-value">
                <span style={{ 
                  color: lead.allow_facebook ? 'var(--success)' : 'var(--error)',
                  fontWeight: 'var(--font-weight-semibold)'
                }}>
                  {lead.allow_facebook ? '✓ Allowed' : '✗ Not Allowed'}
                </span>
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Instagram:</span>
              <span className="detail-value">
                <span style={{ 
                  color: lead.allow_instagram ? 'var(--success)' : 'var(--error)',
                  fontWeight: 'var(--font-weight-semibold)'
                }}>
                  {lead.allow_instagram ? '✓ Allowed' : '✗ Not Allowed'}
                </span>
              </span>
            </div>
          </div>
        </Card>

        {/* Campaign Assignments */}
        <Card>
          <h2 className="card-title">Campaign Assignments</h2>
          <div className="detail-section">
            {campaigns.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                {campaigns.map(campaign => (
                  <div 
                    key={campaign.id}
                    style={{
                      padding: 'var(--spacing-md)',
                      background: 'var(--bg-secondary)',
                      borderRadius: 'var(--border-radius)',
                      border: `1px solid ${campaign.active ? 'var(--success)' : 'var(--border-color)'}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <Link 
                        to={`/campaigns/${campaign.id}/steps`}
                        style={{ 
                          color: 'var(--primary)', 
                          textDecoration: 'none',
                          fontWeight: 'var(--font-weight-semibold)'
                        }}
                      >
                        {campaign.name}
                      </Link>
                      {campaign.goal && (
                        <div style={{ 
                          fontSize: 'var(--font-size-sm)', 
                          color: 'var(--text-secondary)',
                          marginTop: '4px'
                        }}>
                          {campaign.goal}
                        </div>
                      )}
                    </div>
                    <span 
                      style={{ 
                        fontSize: 'var(--font-size-xs)',
                        padding: '4px 12px',
                        borderRadius: 'var(--border-radius)',
                        background: campaign.active ? 'rgba(5, 150, 105, 0.15)' : 'rgba(107, 114, 128, 0.15)',
                        color: campaign.active ? 'var(--success)' : 'var(--text-secondary)',
                        fontWeight: 'var(--font-weight-semibold)'
                      }}
                    >
                      {campaign.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ 
                padding: 'var(--spacing-lg)', 
                textAlign: 'center',
                color: 'var(--text-secondary)'
              }}>
                This lead is not assigned to any campaigns.
              </div>
            )}
          </div>
        </Card>

        {/* Personal Messages */}
        {campaigns.some(c => c.use_personal_messages) && (
          <Card>
            <h2 className="card-title">Personal Messages</h2>
            <div className="detail-section">
              {campaigns.filter(c => c.use_personal_messages).map(campaign => {
                const steps = campaignSteps[campaign.id] || [];
                return (
                  <div key={campaign.id} style={{ marginBottom: 'var(--spacing-lg)' }}>
                    <h3 style={{ 
                      fontSize: 'var(--font-size-base)', 
                      fontWeight: 'var(--font-weight-semibold)',
                      marginBottom: 'var(--spacing-sm)',
                      color: 'var(--text-primary)'
                    }}>
                      {campaign.name}
                    </h3>
                    {steps.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                        {steps.map(step => {
                          const messageKey = `${campaign.id}_${step.step_order}`;
                          const isEditing = editingMessage?.campaignId === campaign.id && editingMessage?.stepOrder === step.step_order;
                          const currentMessage = personalMessages[campaign.id]?.[step.step_order] || '';
                          
                          return (
                            <div key={step.id} style={{
                              padding: 'var(--spacing-md)',
                              background: 'var(--bg-secondary)',
                              borderRadius: 'var(--border-radius)',
                              border: '1px solid var(--border-color)'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-xs)' }}>
                                <div>
                                  <strong style={{ color: 'var(--text-primary)' }}>Step {step.step_order}</strong>
                                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)', marginTop: '2px' }}>
                                    {step.channels?.join(', ') || step.channel || 'No channels'}
                                  </div>
                                </div>
                                {!isEditing && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setEditingMessage({ campaignId: campaign.id, stepOrder: step.step_order })}
                                  >
                                    {currentMessage ? 'Edit' : 'Add Message'}
                                  </Button>
                                )}
                              </div>
                              {isEditing ? (
                                <div>
                                  <textarea
                                    value={currentMessage}
                                    onChange={(e) => {
                                      const newMessages = { ...personalMessages };
                                      if (!newMessages[campaign.id]) {
                                        newMessages[campaign.id] = {};
                                      }
                                      newMessages[campaign.id][step.step_order] = e.target.value;
                                      setPersonalMessages(newMessages);
                                    }}
                                    placeholder={`Leave empty to use campaign template: "${campaign.message_template_name || 'Default Template'}"`}
                                    rows={4}
                                    style={{
                                      width: '100%',
                                      padding: 'var(--spacing-sm)',
                                      fontSize: 'var(--font-size-sm)',
                                      fontFamily: 'var(--font-family)',
                                      border: '1px solid var(--border-color)',
                                      borderRadius: 'var(--border-radius)',
                                      marginBottom: 'var(--spacing-sm)'
                                    }}
                                  />
                                  <div style={{ display: 'flex', gap: 'var(--spacing-sm)', justifyContent: 'flex-end' }}>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setEditingMessage(null);
                                        // Reset to original
                                        const original = lead?.personal_messages || {};
                                        setPersonalMessages(original);
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      onClick={async () => {
                                        try {
                                          setActionLoading(`save_message_${messageKey}`);
                                          await leadsAPI.update(leadId, {
                                            personal_messages: personalMessages
                                          });
                                          toast.success('Personal message saved');
                                          setEditingMessage(null);
                                          await loadLeadData(false);
                                        } catch (error) {
                                          console.error('Failed to save personal message:', error);
                                          toast.error('Failed to save personal message');
                                        } finally {
                                          setActionLoading(null);
                                        }
                                      }}
                                      loading={actionLoading === `save_message_${messageKey}`}
                                    >
                                      Save
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ 
                                  fontSize: 'var(--font-size-sm)', 
                                  color: currentMessage ? 'var(--text-primary)' : 'var(--text-secondary)',
                                  fontStyle: currentMessage ? 'normal' : 'italic',
                                  padding: 'var(--spacing-sm)',
                                  background: currentMessage ? 'rgba(5, 150, 105, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                                  borderRadius: 'var(--border-radius)'
                                }}>
                                  {currentMessage || `Will use campaign template: "${campaign.message_template_name || 'Default Template'}"`}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                        No steps configured for this campaign yet.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>

      {/* Outreach History */}
      <Card>
        <h2 className="card-title">Outreach History</h2>
        {logs.length > 0 ? (
          <div className="outreach-timeline">
            {logs.map((log, index) => (
              <div key={log.id} className="timeline-item">
                <div className="timeline-marker" style={{
                  background: getLogStatusColor(log.status) || 'var(--text-secondary)'
                }} />
                <div className="timeline-content">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--spacing-xs)' }}>
                    <div>
                      <strong style={{ color: 'var(--text-primary)' }}>
                        {log.campaign_name} - Step {log.step_order}
                      </strong>
                      <div style={{ 
                        fontSize: 'var(--font-size-sm)', 
                        color: 'var(--text-secondary)',
                        marginTop: '4px'
                      }}>
                        {log.channel} • {new Date(log.sent_at).toLocaleString()}
                      </div>
                    </div>
                    <span 
                      className="status-badge" 
                      style={{ 
                        background: `${getLogStatusColor(log.status)}20`,
                        color: getLogStatusColor(log.status),
                        border: `1px solid ${getLogStatusColor(log.status)}40`,
                        padding: '4px 12px',
                        borderRadius: 'var(--border-radius)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 'var(--font-weight-semibold)'
                      }}
                    >
                      {log.status}
                    </span>
                  </div>
                  {log.provider_message_id && (
                    <div style={{ 
                      fontSize: 'var(--font-size-xs)', 
                      color: 'var(--text-secondary)',
                      marginTop: '4px'
                    }}>
                      Message ID: {log.provider_message_id}
                    </div>
                  )}
                  {log.error_message && (
                    <div style={{ 
                      fontSize: 'var(--font-size-sm)', 
                      color: 'var(--error)',
                      marginTop: '8px',
                      padding: 'var(--spacing-sm)',
                      background: 'rgba(220, 38, 38, 0.1)',
                      borderRadius: 'var(--border-radius)'
                    }}>
                      Error: {log.error_message}
                    </div>
                  )}
                  {log.reply_received && log.reply_text && (
                    <div style={{ 
                      fontSize: 'var(--font-size-sm)', 
                      color: 'var(--success)',
                      marginTop: '8px',
                      padding: 'var(--spacing-sm)',
                      background: 'rgba(5, 150, 105, 0.1)',
                      borderRadius: 'var(--border-radius)'
                    }}>
                      <strong>Reply:</strong> {log.reply_text}
                    </div>
                  )}
                  {/* Engagement Indicators */}
                  <div style={{ 
                    display: 'flex', 
                    gap: 'var(--spacing-sm)', 
                    marginTop: '8px',
                    flexWrap: 'wrap'
                  }}>
                    {log.message_seen && (
                      <span style={{
                        fontSize: 'var(--font-size-xs)',
                        padding: '4px 8px',
                        borderRadius: 'var(--border-radius)',
                        background: 'rgba(59, 130, 246, 0.15)',
                        color: 'var(--primary)',
                        fontWeight: 'var(--font-weight-semibold)'
                      }}>
                        👁️ Seen {log.seen_at ? `at ${new Date(log.seen_at).toLocaleString()}` : ''}
                      </span>
                    )}
                    {log.engaged && log.engagement_type && (
                      <span style={{
                        fontSize: 'var(--font-size-xs)',
                        padding: '4px 8px',
                        borderRadius: 'var(--border-radius)',
                        background: 'rgba(5, 150, 105, 0.15)',
                        color: 'var(--success)',
                        fontWeight: 'var(--font-weight-semibold)'
                      }}>
                        ✨ {log.engagement_type}
                        {log.engagement_at && ` at ${new Date(log.engagement_at).toLocaleString()}`}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ 
            padding: 'var(--spacing-xl)', 
            textAlign: 'center',
            color: 'var(--text-secondary)'
          }}>
            No outreach history yet. This lead hasn't been contacted through any campaigns.
          </div>
        )}
      </Card>
    </div>
  );
}
